import {
  evaluatePermission,
  projectVisibleFields,
  type FieldVisibilityRule,
  type ObjectScope,
  type PermissionContext,
  type PermissionPolicy,
  type PermissionResult,
} from '../access/permissionEngine.ts'
import type { HumanRoleId } from '../access/rolePreset.ts'
import type { ConfigurationLayerId, ResolvedConfiguration } from '../configuration/configurationResolver.ts'

export type PreviewWarningCode =
  | 'configuration_missing'
  | 'configuration_invalid'
  | 'configuration_expired'

export type PreviewConfigurationState = Readonly<{
  valid: boolean
  expiresAt?: string | null
}>

export type PreviewPolicy = Readonly<{
  purpose: 'admin_explainability'
  objectScope: ObjectScope
}>

export type PreviewActionInput = Readonly<{
  id: string
  context: PermissionContext
  policies: readonly PermissionPolicy[]
}>

export type PreviewFieldInput = Readonly<{
  record: Readonly<Record<string, unknown>>
  rules: readonly FieldVisibilityRule[]
}>

export type ResolvedExperiencePreviewInput = Readonly<{
  userId: string
  roleId: HumanRoleId
  configuration: ResolvedConfiguration
  configurationState: PreviewConfigurationState
  previewPolicy: PreviewPolicy
  enabledModules: ReadonlySet<string>
  pinnedMetric?: string | null
  actions: readonly PreviewActionInput[]
  fields?: PreviewFieldInput
  now?: Date
}>

export type PreviewAction = Readonly<{
  id: string
  available: boolean
  decision: PermissionResult['decision']
  reasonCode: PermissionResult['reasonCode']
}>

export type ResolvedExperiencePreview = Readonly<{
  mode: 'read_only_preview'
  purpose: PreviewPolicy['purpose']
  userId: string
  roleId: HumanRoleId
  objectScope: ObjectScope
  navigation: readonly string[]
  modules: readonly string[]
  homeSections: readonly string[]
  defaultView: string | null
  pinnedMetric: string | null
  visibleFields: Readonly<Record<string, unknown>>
  actions: readonly PreviewAction[]
  provenance: Readonly<Record<string, ConfigurationLayerId | 'missing'>>
  warnings: readonly PreviewWarningCode[]
}>

function stringList(value: unknown): readonly string[] {
  if (!Array.isArray(value)) return []
  return Object.freeze(value.filter((item): item is string => typeof item === 'string'))
}

function optionalString(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value : null
}

function navigationModuleId(item: string): string | null {
  return item.startsWith('module:') ? item.slice('module:'.length) : null
}

function configurationWarnings(
  configuration: ResolvedConfiguration,
  state: PreviewConfigurationState,
  now: Date,
): readonly PreviewWarningCode[] {
  const warnings: PreviewWarningCode[] = []
  if (configuration.missingKeys.length > 0) warnings.push('configuration_missing')
  if (!state.valid) warnings.push('configuration_invalid')
  if (state.expiresAt) {
    const expiresAt = Date.parse(state.expiresAt)
    if (Number.isFinite(expiresAt) && expiresAt <= now.getTime()) warnings.push('configuration_expired')
  }
  return Object.freeze(warnings)
}

export function buildResolvedExperiencePreview(input: ResolvedExperiencePreviewInput): ResolvedExperiencePreview {
  const configuredNavigation = stringList(input.configuration.values.navigation)
  const navigation = configuredNavigation.filter((item) => {
    const moduleId = navigationModuleId(item)
    return moduleId === null || input.enabledModules.has(moduleId)
  })

  const actions = input.actions.map((item): PreviewAction => {
    const result = evaluatePermission(item.context, item.policies)
    return Object.freeze({
      id: item.id,
      available: result.decision === 'ALLOW' || result.decision === 'DELEGATED',
      decision: result.decision,
      reasonCode: result.reasonCode,
    })
  })

  const visibleFields = input.fields
    ? projectVisibleFields({ ...input.fields.record }, input.roleId, input.fields.rules)
    : {}

  return Object.freeze({
    mode: 'read_only_preview' as const,
    purpose: input.previewPolicy.purpose,
    userId: input.userId,
    roleId: input.roleId,
    objectScope: Object.freeze({ ...input.previewPolicy.objectScope }),
    navigation: Object.freeze(navigation),
    modules: Object.freeze([...input.enabledModules].sort()),
    homeSections: Object.freeze([...stringList(input.configuration.values.homeSections)]),
    defaultView: optionalString(input.configuration.values.defaultView),
    pinnedMetric: optionalString(input.pinnedMetric),
    visibleFields: Object.freeze({ ...visibleFields }),
    actions: Object.freeze(actions),
    provenance: Object.freeze({ ...input.configuration.provenance }),
    warnings: configurationWarnings(input.configuration, input.configurationState, input.now ?? new Date()),
  })
}
